import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articleTypesApi, articlesApi, type ArticleTypeView, type ArticleView } from "../api/articles";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { Pagination } from "../components/Pagination";
import { SortSelect, SortableTh, sortParam, type SortState } from "../components/SortableTh";
import { Select, type SelectOption } from "../components/Select";
import { ArticleFormModal } from "../components/ArticleFormModal";
import Ic from "../components/icons.jsx";

const PAGE_SIZE = 25;

/** Every column of the list sorts on the server (Strahinja, 2026-10-12); the keys are ArticleView fields. */
type ArticleSortField = "code" | "name" | "typeName" | "unit" | "manufacturer" | "positionCount";
const ALL = "";

/** `/artikli` — the article catalogue (sprint 5l §3, §7): search, filter by type, "Nov artikal". */
export default function ArticlesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("articles:create");
  // The type filter draws on the neighbouring module: only with its view (audit 2026-09-23 §B2).
  const canFilterTypes = hasPermission("article-types:view");
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState(params.get("q") ?? "");
  const [debouncedQ, setDebouncedQ] = useState(q.trim());
  const [typeId, setTypeId] = useState(params.get("typeId") ?? ALL);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortState<ArticleSortField>>({ field: "code", dir: "asc" });

  const [types, setTypes] = useState<ArticleTypeView[]>([]);
  const [items, setItems] = useState<ArticleView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    if (!canFilterTypes) return;
    let cancelled = false;
    articleTypesApi.all()
      .then((list) => { if (!cancelled) setTypes(list); })
      .catch(() => { if (!cancelled) setTypes([]); });
    return () => { cancelled = true; };
  }, [canFilterTypes]);

  // Mirror the filters into the URL so the list survives back navigation and deep links (`?typeId=` from a type's card).
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    if (typeId) next.set("typeId", typeId);
    setParams(next, { replace: true });
  }, [debouncedQ, typeId, setParams]);

  // A filter or sort change restarts at the first page — without an extra fetch of the stale page in between.
  const filterKey = `${debouncedQ}|${typeId}|${sortParam(sort)}`;
  const prevFilterKey = useRef(filterKey);

  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      if (page !== 0) { setPage(0); return; }
    }
    let cancelled = false;
    setError(null);
    articlesApi
      .list({ q: debouncedQ || undefined, typeId: typeId || undefined, page, size: PAGE_SIZE, sort: sortParam(sort) })
      .then((res) => {
        if (cancelled) return;
        setItems(res.content);
        setTotal(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setItems([]);
      });
    return () => { cancelled = true; };
    // filterKey already encodes every filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page]);

  const typeOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: ALL, label: t("articles.filter.allTypes", "Svi tipovi artikala") },
      ...types.map((x) => ({ value: x.id, label: x.name, keywords: x.code })),
    ],
    [types, t],
  );

  const hasFilters = debouncedQ !== "" || typeId !== ALL;
  const colCode = t("articles.col.code", "Šifra");
  const colName = t("articles.col.name", "Naziv");
  const colType = t("articles.col.type", "Tip");
  const colUnit = t("articles.col.unit", "Jedinica");
  const colManufacturer = t("articles.col.manufacturer", "Proizvođač");
  const colPositions = t("articles.col.positionCount", "Na pozicija");
  const dash = <span className="muted">—</span>;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("articles.title", "Artikli")}</h1>
          <div className="sub">
            {items === null
              ? t("common.loading", "Učitavanje…")
              : t("articles.count", "{{count}} artikala u katalogu", { count: total })}
          </div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
              <Ic.plus /> {t("articles.new", "Nov artikal")}
            </button>
          </div>
        )}
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("articles.filter.searchPlaceholder", "Pretraga po šifri, nazivu ili tipu…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {canFilterTypes && (
          <Select<string>
            value={typeId}
            options={typeOptions}
            onChange={setTypeId}
            ariaLabel={t("articles.filter.type", "Tip artikla")}
            style={{ flex: "0 1 260px", minWidth: "min(200px, 100%)" }}
          />
        )}
        <SortSelect
          fields={[
            { field: "code", label: colCode },
            { field: "name", label: colName },
            { field: "typeName", label: colType },
            { field: "unit", label: colUnit },
            { field: "manufacturer", label: colManufacturer },
            { field: "positionCount", label: colPositions },
          ]}
          sort={sort}
          onSort={setSort}
        />
        {hasFilters && (
          <button type="button" className="btn sm ghost" onClick={() => { setQ(""); setTypeId(ALL); }}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="card">
        {items === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : items.length === 0 ? (
          <div className="empty">
            {hasFilters
              ? t("articles.emptyFiltered", "Nijedan artikal ne odgovara pretrazi.")
              : t("articles.empty", "Katalog artikala je prazan. Dodajte prvi artikal.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <SortableTh field="code" sort={sort} onSort={setSort}>{colCode}</SortableTh>
                  <SortableTh field="name" sort={sort} onSort={setSort}>{colName}</SortableTh>
                  <SortableTh field="typeName" sort={sort} onSort={setSort}>{colType}</SortableTh>
                  <SortableTh field="unit" sort={sort} onSort={setSort}>{colUnit}</SortableTh>
                  <SortableTh field="manufacturer" sort={sort} onSort={setSort} className="hide-tablet">{colManufacturer}</SortableTh>
                  <SortableTh field="positionCount" sort={sort} onSort={setSort} className="num">{colPositions}</SortableTh>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="is-clickable"
                    onClick={() => navigate(`/artikli/${it.id}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/artikli/${it.id}`); }}
                    tabIndex={0}
                  >
                    <td className="mono" data-label={colCode}>{it.code}</td>
                    <td data-label={colName}><b>{it.name}</b></td>
                    <td data-label={colType}>{it.typeName ?? dash}</td>
                    <td data-label={colUnit}>{it.unit}</td>
                    <td className="hide-tablet" data-label={colManufacturer}>{it.manufacturer ?? dash}</td>
                    <td className="num" data-label={colPositions}>{it.positionCount > 0 ? it.positionCount : dash}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      {createOpen && (
        <ArticleFormModal
          initialTypeId={typeId || null}
          onClose={() => setCreateOpen(false)}
          onSaved={(saved) => {
            setCreateOpen(false);
            toast.success(t("articles.toast.created", "Artikal kreiran."));
            navigate(`/artikli/${saved.id}`);
          }}
        />
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
